/**
 * Spec 087 (gap 087-2) — Per-URL page inventory table.
 *
 * Virtualized with `react-window@^2.2.7` which exports `List` (not the
 * legacy v1 `FixedSizeList`). Columns: URL, Tier, Eval status, Clusters,
 * Last modified, Evaluation detail link.
 *
 * Filtering: tier + evaluation_status, both optional. A single fetch
 * request returns up to 500 rows (the server cap); total is rendered
 * as a footer note when it exceeds the cap.
 */
import { useEffect, useState, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { List } from 'react-window';
import { Icon, Spinner } from '../ui/Icon.js';
import {
  listSitemapUrls,
  type ListUrlsFilters,
  type SitemapUrlRow,
  type SitemapUrlTier,
  type SitemapUrlEvaluationStatus,
} from '../../api/sitemapAnalyses.js';

const ROW_HEIGHT = 48;
const LIST_HEIGHT = 480;

const TIER_CLASSES: Record<SitemapUrlTier, string> = {
  tier_1: 'bg-violet-100 text-violet-800 border-violet-200',
  tier_2: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  tier_3: 'bg-slate-100 text-slate-700 border-slate-200',
  tier_4: 'bg-slate-50 text-slate-500 border-slate-200',
};

const TIER_LABEL: Record<SitemapUrlTier, string> = {
  tier_1: 'T1',
  tier_2: 'T2',
  tier_3: 'T3',
  tier_4: 'T4',
};

const STATUS_CLASSES: Record<SitemapUrlEvaluationStatus, string> = {
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  evaluating: 'bg-amber-100 text-amber-800 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-rose-100 text-rose-800 border-rose-200',
  skipped: 'bg-slate-100 text-slate-600 border-slate-200',
};

interface UrlInventoryTableProps {
  analysisId: string;
}

interface UrlRowExtraProps {
  rows: SitemapUrlRow[];
}

interface UrlRowRenderProps extends UrlRowExtraProps {
  index: number;
  style: CSSProperties;
}

function UrlRowRenderer({ index, style, rows }: UrlRowRenderProps) {
  const row = rows[index];
  if (!row) return null;
  const clusters = row.assigned_cluster_slugs;
  const extra = clusters.length > 2 ? clusters.length - 2 : 0;
  const detailTarget = row.page_evaluation_id
    ? `/admin/evaluations/${row.page_evaluation_id}`
    : null;
  const lastmod = row.lastmod ? new Date(row.lastmod).toLocaleDateString() : '—';

  // Spec 089c — render a "GSC ground truth" pill when the URL has impressions
  // in the last 28 days (the GSC tier-upgrade post-pass writes this signal).
  const gscImpressionsRaw = row.triage_signals
    ? (row.triage_signals as Record<string, unknown>)['gsc_impressions_28d']
    : undefined;
  const gscImpressions =
    typeof gscImpressionsRaw === 'number' && gscImpressionsRaw > 0
      ? gscImpressionsRaw
      : null;

  return (
    <div
      style={style}
      data-testid={`url-inventory-row-${row.id}`}
      className="flex items-center gap-3 border-b border-slate-100 px-3 text-sm hover:bg-slate-50"
    >
      {/* URL — 50% */}
      <div className="flex-[2] min-w-0">
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          title={row.url}
          className="block truncate text-slate-700 hover:text-violet-700 hover:underline"
        >
          {row.url}
        </a>
      </div>
      {/* Tier + (089c) GSC ground-truth pill */}
      <div className="flex w-20 flex-shrink-0 items-center gap-1">
        <span
          className={`inline-flex items-center justify-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${TIER_CLASSES[row.tier]}`}
        >
          {TIER_LABEL[row.tier]}
        </span>
        {gscImpressions !== null && (
          <span
            data-testid={`url-inventory-gsc-pill-${row.id}`}
            title={`GSC ground truth — ${gscImpressions.toLocaleString()} impressions on Google AI surfaces in the last 28 days`}
            className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-xs font-medium text-teal-700"
          >
            GSC
          </span>
        )}
      </div>
      {/* Evaluation status */}
      <div className="w-24 flex-shrink-0">
        <span
          className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium ${STATUS_CLASSES[row.evaluation_status]}`}
        >
          {row.evaluation_status}
        </span>
      </div>
      {/* Clusters */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {clusters.slice(0, 2).map((slug) => (
          <span
            key={slug}
            className="inline-flex max-w-[8rem] truncate rounded-full bg-teal-50 px-1.5 py-0.5 text-xs text-teal-800"
            title={slug}
          >
            {slug}
          </span>
        ))}
        {extra > 0 && (
          <span className="text-xs text-slate-500">+{extra} more</span>
        )}
        {clusters.length === 0 && (
          <span className="text-xs text-slate-400">—</span>
        )}
      </div>
      {/* Last modified */}
      <div className="w-24 flex-shrink-0 tabular-nums text-xs text-slate-500">
        {lastmod}
      </div>
      {/* Evaluation detail link */}
      <div className="w-8 flex-shrink-0">
        {detailTarget ? (
          <Link
            to={detailTarget}
            aria-label="Open evaluation detail"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
          >
            <Icon name="bx-right-arrow-alt" />
          </Link>
        ) : (
          <span
            aria-label="No evaluation yet"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-100 text-slate-300"
          >
            <Icon name="bx-right-arrow-alt" />
          </span>
        )}
      </div>
    </div>
  );
}

export function UrlInventoryTable({ analysisId }: UrlInventoryTableProps) {
  const [rows, setRows] = useState<SitemapUrlRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState<SitemapUrlTier | ''>('');
  const [status, setStatus] = useState<SitemapUrlEvaluationStatus | ''>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: ListUrlsFilters = { limit: 500, offset: 0 };
      if (tier) filters.tier = tier;
      if (status) filters.evaluation_status = status;
      const res = await listSitemapUrls(analysisId, filters);
      setRows(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load URLs');
    } finally {
      setLoading(false);
    }
  }, [analysisId, tier, status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      data-testid="url-inventory-table"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Page inventory</h3>
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-1">
            <span className="text-xs text-slate-500">Tier</span>
            <select
              data-testid="url-inventory-tier-filter"
              value={tier}
              onChange={(e) => setTier(e.target.value as SitemapUrlTier | '')}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            >
              <option value="">Any</option>
              <option value="tier_1">Tier 1</option>
              <option value="tier_2">Tier 2</option>
              <option value="tier_3">Tier 3</option>
              <option value="tier_4">Tier 4</option>
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span className="text-xs text-slate-500">Status</span>
            <select
              data-testid="url-inventory-status-filter"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as SitemapUrlEvaluationStatus | '')
              }
              className="rounded-md border border-slate-300 px-2 py-1 text-xs"
            >
              <option value="">Any</option>
              <option value="skipped">Skipped</option>
              <option value="pending">Pending</option>
              <option value="evaluating">Evaluating</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </label>
        </div>
      </header>

      {/* Column headers */}
      <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
        <div className="flex-[2]">URL</div>
        <div className="w-20 flex-shrink-0">Tier</div>
        <div className="w-24 flex-shrink-0">Status</div>
        <div className="min-w-0 flex-1">Clusters</div>
        <div className="w-24 flex-shrink-0">Last mod</div>
        <div className="w-8 flex-shrink-0" aria-hidden />
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Spinner  />
        </div>
      ) : error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </div>
      ) : total === 0 ? (
        <div className="py-12 text-center text-sm text-slate-500">
          No URLs match the current filters.
        </div>
      ) : (
        <>
          <List<UrlRowExtraProps>
            rowCount={rows.length}
            rowHeight={ROW_HEIGHT}
            rowProps={{ rows }}
            rowComponent={UrlRowRenderer}
            style={{ height: LIST_HEIGHT, width: '100%' }}
            overscanCount={8}
          />
          {total > rows.length && (
            <p className="mt-2 text-xs text-slate-500">
              Showing first {rows.length} of {total} — refine filters to narrow
              the view.
            </p>
          )}
        </>
      )}
    </section>
  );
}
